import type { Descendant } from 'platejs'

import { deserializeRichTextMarkdown } from '@/lib/rich-text-visible-char-count'

/** Plate markdown serialize uses U+200B in empty blocks — not user-visible content. */
const MARKDOWN_FIELD_ZWSP = '\u200B'

/** Section titles that duplicate an outer heading when repeated as the first markdown ATX heading. */
export const REDUNDANT_MARKDOWN_SECTION_HEADINGS = [
  'job description',
  'about this role',
  'role description',
  'about',
] as const

function normalizeHeadingText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/[.:]+$/, '')
}

/**
 * Drop a leading `# …` line when it repeats a title already shown outside the markdown body
 * (e.g. job title in identity header, or "Job description" on the section card).
 */
export function stripRedundantLeadingHeading(
  markdown: string,
  matchTitles: Array<string | null | undefined> = [],
): string {
  const trimmed = markdown.trim()
  if (!trimmed) return trimmed

  const lines = trimmed.split('\n')
  const firstContentIndex = lines.findIndex((line) => line.trim().length > 0)
  if (firstContentIndex === -1) return trimmed

  const headingMatch = lines[firstContentIndex].match(/^#{1,6}\s+(.+?)\s*$/)
  if (!headingMatch) return trimmed

  const headingText = normalizeHeadingText(headingMatch[1])
  const candidates = new Set<string>([
    ...REDUNDANT_MARKDOWN_SECTION_HEADINGS,
    ...matchTitles.filter((title): title is string => Boolean(title?.trim())).map(normalizeHeadingText),
  ])

  const isRedundant = [...candidates].some((candidate) => {
    if (!candidate) return false
    return headingText === candidate || headingText.startsWith(`${candidate} `)
  })

  if (!isRedundant) return trimmed

  const rest = lines.slice(firstContentIndex + 1)
  while (rest.length > 0 && rest[0].trim() === '') {
    rest.shift()
  }

  return rest.join('\n').trim()
}

function collectPlatePlainText(nodes: Descendant[]): string {
  const parts: string[] = []
  const walk = (node: Descendant) => {
    if ('text' in node && typeof node.text === 'string') {
      const text = node.text.split(MARKDOWN_FIELD_ZWSP).join('')
      if (text.trim() === '') return
      parts.push(text)
    }
    if ('children' in node && Array.isArray(node.children)) {
      node.children.forEach(walk)
    }
  }
  nodes.forEach(walk)
  return parts.join(' ').replace(/\s+/g, ' ').trim()
}

/**
 * Plain-text teaser from markdown for list cards — same visible text as Preview,
 * without `#` / `**` / link syntax. Optionally drop a leading heading that repeats
 * an outer title (e.g. job title already on the card).
 */
export function markdownPlainTextPreview(
  markdown: string | null | undefined,
  matchTitles: Array<string | null | undefined> = [],
): string {
  const stripped = stripRedundantLeadingHeading(markdown?.trim() ?? '', matchTitles)
  if (!stripped) return ''
  return collectPlatePlainText(deserializeRichTextMarkdown(stripped))
}
